/**
 * First-class Owner statement — Reports + Hub Accounting.
 */
import { useEffect, useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { formatMoney } from '../../lib/formatters'
import StatementActions from '../../components/statements/StatementActions'
import {
  fetchStatementOwners,
  fetchStatementPreview,
} from '../../api/statements'
import { getPayAppPdfUrl, getToken } from '../../api/payApps'
import { resolveOwnerPicker } from './resolveOwnerPicker.js'

type PaRow = {
  id?: number
  app_number?: number
  submitted_at?: string
  created_at?: string
  payment_due_date?: string
  days_past_due?: number | null
  balance?: number
  project_name?: string
}

type Section = {
  project_id?: number
  project_name?: string
  project_number?: string
  openPayApps?: PaRow[]
  openTotal?: number
}

export default function OwnerStatementScreen() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const ownerFromUrl = params.get('owner')?.trim() ?? ''
  const ownerIdParam = params.get('owner_id')
  const ownerIdFromUrl = ownerIdParam ? parseInt(ownerIdParam, 10) : null

  const ownersQ = useQuery({
    queryKey: ['statement-owners'],
    queryFn: fetchStatementOwners,
    staleTime: 30_000,
  })

  const picked = resolveOwnerPicker({
    ownerFromUrl,
    ownerIdFromUrl: ownerIdFromUrl != null && Number.isFinite(ownerIdFromUrl) ? ownerIdFromUrl : null,
    owners: ownersQ.data,
  })
  const ownerName = picked.ownerName
  const ownerId = picked.ownerId

  useEffect(() => {
    if (!ownersQ.data?.length) return
    const next = new URLSearchParams(params)
    let changed = false
    if (ownerName && next.get('owner') !== ownerName) {
      next.set('owner', ownerName)
      changed = true
    }
    if (ownerId != null && next.get('owner_id') !== String(ownerId)) {
      next.set('owner_id', String(ownerId))
      changed = true
    }
    if (changed) setParams(next, { replace: true })
  }, [ownerName, ownerId, ownersQ.data, params, setParams])

  const previewQ = useQuery({
    queryKey: ['statement-preview', ownerName, ownerId],
    queryFn: () => fetchStatementPreview({
      direction: 'owner',
      owner_name: ownerName || undefined,
      owner_id: ownerId && Number.isFinite(ownerId) ? ownerId : undefined,
    }),
    enabled: Boolean(ownerName || ownerId),
  })

  const payload = previewQ.data as {
    ownerName?: string
    billingContact?: { name?: string; email?: string; email_is_placeholder?: boolean }
    emailPending?: boolean
    generatedAt?: string
    openTotal?: number
    projectSections?: Section[]
    openPayApps?: PaRow[]
  } | undefined

  const sections = payload?.projectSections || []
  const displayName = payload?.ownerName || ownerName || 'Owner'
  const ownerEmail = payload?.billingContact?.email || undefined
  const emailPending = Boolean(payload?.emailPending || payload?.billingContact?.email_is_placeholder)

  const statementParams = useMemo(() => ({
    owner_name: displayName,
    ...(ownerId && Number.isFinite(ownerId) ? { owner_id: ownerId } : {}),
  }), [displayName, ownerId])

  return (
    <>
      <div className="topbar">
        <div className="crumb">
          <span style={{ cursor: 'pointer' }} onClick={() => navigate('/reports')}>Reports</span>
          {' · '}
          <b>Owner statement</b>
        </div>
      </div>
      <div className="pc" data-testid="owner-statement-screen">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          <div>
            <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 22, margin: '0 0 8px' }}>
              Owner statement{displayName ? ` · ${displayName}` : ''}
            </h1>
            <label style={{ fontSize: 13 }}>
              Owner{' '}
              <select
                data-testid="owner-statement-picker"
                value={ownerId != null ? `id:${ownerId}` : `name:${ownerName}`}
                onChange={(e) => {
                  const v = e.target.value
                  const next = new URLSearchParams()
                  if (v.startsWith('id:')) {
                    const id = v.slice(3)
                    const match = ownersQ.data?.find((o) => String(o.owner_id) === id)
                    next.set('owner_id', id)
                    if (match?.owner_name) next.set('owner', match.owner_name)
                  } else {
                    next.set('owner', v.slice(5))
                  }
                  setParams(next)
                }}
              >
                {(ownersQ.data || []).map((o) => (
                  <option
                    key={`${o.owner_id}-${o.owner_name}`}
                    value={o.owner_id != null ? `id:${o.owner_id}` : `name:${o.owner_name}`}
                  >
                    {o.owner_name} ({formatMoney(Number(o.open_total) || 0)})
                  </option>
                ))}
              </select>
            </label>
          </div>
          <StatementActions
            direction="owner"
            label="Statement PDF"
            variant="primary"
            testId="owner-statement-export-all"
            ownerEmail={ownerEmail}
            emailPending={emailPending}
            params={statementParams}
          />
        </div>

        {(ownersQ.isLoading || previewQ.isLoading) && <p>Loading statement…</p>}
        {previewQ.isError && <p style={{ color: 'var(--red)' }}>Could not load statement.</p>}
        {!ownersQ.isLoading && !previewQ.isLoading && !previewQ.isError && !payload && (
          <p data-testid="owner-statement-empty">No owners with open pay apps.</p>
        )}

        {payload && (
          <div className="panel" style={{ padding: 16 }} data-testid="owner-statement-report">
            <div style={{ fontSize: 13, color: 'var(--ink3)', marginBottom: 8 }}>
              {displayName}
              {payload.billingContact?.name ? ` · ${payload.billingContact.name}` : ''}
              {payload.generatedAt ? ` · as of ${String(payload.generatedAt).slice(0, 10)}` : ''}
            </div>
            {sections.map((s) => (
              <div key={s.project_id || s.project_name} data-testid="os-project-group" style={{ marginBottom: 18 }}>
                <h2 style={{ fontSize: 16, margin: '0 0 8px' }}>{s.project_name}</h2>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th align="left">Pay app</th>
                      <th align="left">Submitted</th>
                      <th align="left">Due</th>
                      <th align="left">Days past due</th>
                      <th align="right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(s.openPayApps || []).map((row) => (
                      <tr key={row.id || row.app_number} data-testid="os-pa-line">
                        <td>
                          {row.id ? (
                            <a
                              href={getPayAppPdfUrl(row.id, getToken() ?? '')}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid="os-pa-pdf"
                            >
                              PA #{row.app_number}
                            </a>
                          ) : (
                            <>PA #{row.app_number}</>
                          )}
                        </td>
                        <td>{row.submitted_at ? String(row.submitted_at).slice(0, 10) : '—'}</td>
                        <td>{row.payment_due_date ? String(row.payment_due_date).slice(0, 10) : '—'}</td>
                        <td>{row.days_past_due && row.days_past_due > 0 ? `${row.days_past_due}` : 'Current'}</td>
                        <td align="right">{formatMoney(Number(row.balance) || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
            <p data-testid="os-total-due" style={{ fontWeight: 700 }}>
              Total due: {formatMoney(Number(payload.openTotal) || 0)}
            </p>
            <p data-testid="os-ask">Please confirm expected payment date for the items above.</p>
          </div>
        )}

        <p style={{ marginTop: 16, fontSize: 13 }}>
          <Link to="/reports/operations">Operations report</Link>
        </p>
      </div>
    </>
  )
}
